import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  ScrollArea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  Skeleton,
} from '@design-system';
import { Upload, X } from 'lucide-react';
import { type FundingPolicyTypeDef } from '@shared';
import { type useEditFormConfig } from '@/features/quote-and-apply-builder/hooks/use-edit-form-config';

const ADD_NEW_VALUE = '__add_new__';

type EditFormConfigDialogProps = ReturnType<typeof useEditFormConfig>;

export function EditFormConfigDialog({
  isOpen,
  isLoadingConfig,
  title,
  formType,
  selectedCarrierId,
  customCarrier,
  isCustomCarrier,
  selectedProduct,
  customProduct,
  isCustomProduct,
  email,
  selectedPolicyTypes,
  policyTypeFilter,
  stateCode,
  saveError,
  isSaving,
  carriers,
  uniqueProducts,
  filteredPolicyTypes,
  importSource,
  importError,
  isValid,
  fileInputRef,
  dispatch,
  handleClose,
  handleSave,
  handleFileImport,
  handleCarrierChange,
  handleProductChange,
}: EditFormConfigDialogProps) {
  return (
    <Dialog
      open={isOpen}
      onOpenChange={open => {
        if (!open) handleClose();
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit Form Configuration</DialogTitle>
        </DialogHeader>

        {isLoadingConfig ? (
          <div className="space-y-4 py-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="space-y-2"
              >
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-10 w-full" />
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-6 py-2">
            <div className="grid gap-2">
              <Label htmlFor="edit-form-title">
                Form Title <span className="text-destructive">*</span>
              </Label>
              <Input
                id="edit-form-title"
                value={title}
                onChange={event => dispatch({ type: 'SET_TITLE', value: event.target.value })}
                placeholder="e.g., Lincoln MoneyGuard Application"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="edit-form-type">
                Form Type <span className="text-destructive">*</span>
              </Label>
              <Select
                value={formType}
                onValueChange={val =>
                  dispatch({ type: 'SET_FORM_TYPE', value: val as PolicyFormType })
                }
              >
                <SelectTrigger
                  id="edit-form-type"
                  className="w-full"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="application-form">Application Form</SelectItem>
                  <SelectItem value="pre-underwriting-form">Pre-Underwriting Form</SelectItem>
                  <SelectItem value="partner-pre-underwriting-form">
                    Partner Pre-Underwriting Form
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label>
                Carrier <span className="text-destructive">*</span>
              </Label>
              {isCustomCarrier ? (
                <div className="flex gap-2">
                  <Input
                    value={customCarrier}
                    onChange={event =>
                      dispatch({ type: 'SET_CUSTOM_CARRIER', value: event.target.value })
                    }
                    placeholder="Enter carrier name"
                    className="flex-1"
                  />
                  {carriers && carriers.length > 0 && (
                    <Button
                      variant="outline"
                      className="shrink-0"
                      onClick={() => dispatch({ type: 'SELECT_EXISTING_CARRIER' })}
                    >
                      Select existing
                    </Button>
                  )}
                </div>
              ) : (
                <Select
                  value={selectedCarrierId}
                  onValueChange={handleCarrierChange}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a carrier" />
                  </SelectTrigger>
                  <SelectContent>
                    {carriers?.map(carrier => (
                      <SelectItem
                        key={carrier.insuranceCarrierId}
                        value={String(carrier.insuranceCarrierId)}
                      >
                        {carrier.carrierName}
                      </SelectItem>
                    ))}
                    <SelectItem value={ADD_NEW_VALUE}>+ Add new carrier</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="grid gap-2">
              <Label>
                Product <span className="text-destructive">*</span>
              </Label>
              {isCustomProduct || isCustomCarrier ? (
                <div className="flex gap-2">
                  <Input
                    value={customProduct}
                    onChange={event =>
                      dispatch({ type: 'SET_CUSTOM_PRODUCT', value: event.target.value })
                    }
                    placeholder="Enter product name"
                    className="flex-1"
                  />
                  {!isCustomCarrier && uniqueProducts.length > 0 && (
                    <Button
                      variant="outline"
                      className="shrink-0"
                      onClick={() => dispatch({ type: 'SELECT_EXISTING_PRODUCT' })}
                    >
                      Select existing
                    </Button>
                  )}
                </div>
              ) : (
                <Select
                  value={selectedProduct}
                  onValueChange={handleProductChange}
                  disabled={!selectedCarrierId}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue
                      placeholder={
                        selectedCarrierId ? 'Select a product' : 'Select a carrier first'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {uniqueProducts.map(product => (
                      <SelectItem
                        key={product}
                        value={product}
                      >
                        {product}
                      </SelectItem>
                    ))}
                    <SelectItem value={ADD_NEW_VALUE}>+ Add new product</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={email}
                onChange={event => dispatch({ type: 'SET_EMAIL', value: event.target.value })}
                placeholder="e.g., submissions@carrier.com"
              />
            </div>

            <Separator />

            <div className="space-y-3">
              <Label>Replace Form Definition</Label>
              {importSource ? (
                <Alert
                  variant="success"
                  className="items-center p-2"
                >
                  <Upload className="ml-2.5 size-4" />
                  <AlertTitle className="ml-2 flex items-center gap-2">
                    <span className="flex-1 truncate">{importSource}</span>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => dispatch({ type: 'CLEAR_IMPORT' })}
                    >
                      <X className="size-4" />
                    </Button>
                  </AlertTitle>
                </Alert>
              ) : (
                <Button
                  variant="outline"
                  className="w-full shrink-0 border-dashed"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="size-4" />
                  Upload JSON
                </Button>
              )}
              {importError && (
                <Alert variant="destructive">
                  <AlertDescription>{importError}</AlertDescription>
                </Alert>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                className="hidden"
                onChange={handleFileImport}
              />
            </div>

            <Separator />

            <div className="space-y-3">
              <Label>
                Policy Types <span className="text-destructive">*</span>
              </Label>
              <Input
                value={policyTypeFilter}
                onChange={event =>
                  dispatch({ type: 'SET_POLICY_TYPE_FILTER', value: event.target.value })
                }
                placeholder="Filter policy types..."
              />
              <ScrollArea className="h-36 rounded-md border p-2">
                {filteredPolicyTypes.map((policyTypeDef: FundingPolicyTypeDef) => (
                  <Label
                    key={policyTypeDef.value}
                    className="hover:bg-muted flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 font-normal"
                  >
                    <Checkbox
                      checked={selectedPolicyTypes.includes(policyTypeDef.value)}
                      onCheckedChange={() =>
                        dispatch({ type: 'TOGGLE_POLICY_TYPE', value: policyTypeDef.value })
                      }
                    />
                    <div className="min-w-0 flex-1">
                      <span className="text-sm font-medium">{policyTypeDef.label}</span>
                      {policyTypeDef.carrier && (
                        <span className="text-muted-foreground ml-1.5 text-xs">
                          ({policyTypeDef.carrier})
                        </span>
                      )}
                    </div>
                  </Label>
                ))}
                {filteredPolicyTypes.length === 0 && (
                  <p className="text-muted-foreground py-4 text-center text-sm">
                    No matching policy types
                  </p>
                )}
              </ScrollArea>
              {selectedPolicyTypes.length > 0 && (
                <p className="text-muted-foreground text-xs">
                  {selectedPolicyTypes.length} selected
                </p>
              )}
            </div>

            <Separator />

            <div className="grid gap-2">
              <Label htmlFor="edit-state-code">State Code</Label>
              <Input
                id="edit-state-code"
                value={stateCode}
                onChange={event =>
                  dispatch({ type: 'SET_STATE_CODE', value: event.target.value })
                }
                placeholder="e.g., CA, NY, FL"
                className="w-full"
              />
            </div>

            {saveError && (
              <Alert variant="destructive">
                <AlertDescription>{saveError}</AlertDescription>
              </Alert>
            )}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!isValid || isSaving || isLoadingConfig}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
